import { createClient } from '@supabase/supabase-js'
import { cookieStorage } from './utils/cookieStorage.js'
import { bypassFetch } from './utils/bypassFetch.js'

const PROD_SUPABASE_URL = 'https://zpawazevsrruppssfhqp.supabase.co'
const _envSupabaseUrl = import.meta.env.VITE_SUPABASE_URL

// vite.config.js replaces __DEPLOY_ENV__ at BUILD time only — in `vite dev` it stays an
// undeclared identifier, so it must be read through `typeof` or the guard below blows up
// with a ReferenceError instead of its own message. Undefined means dev, i.e. not
// production, which is the safe reading anyway.
const DEPLOY_ENV = typeof __DEPLOY_ENV__ === 'undefined' ? 'local' : __DEPLOY_ENV__

// [ENV GUARD] An unset VITE_SUPABASE_URL falls through to production below. On the
// real production deploy that is the intended safety net; anywhere else it means dev
// work quietly reading and WRITING the live database. Refuse loudly instead.
if (!_envSupabaseUrl && DEPLOY_ENV !== 'production') {
    throw new Error(
        `[supabase] VITE_SUPABASE_URL is not set (deploy env: "${DEPLOY_ENV}"). ` +
        `Refusing to fall back to the production database at ${PROD_SUPABASE_URL}. ` +
        `Use \`npm run dev\` (staging) or \`npm run dev:docker\` (local) — ` +
        `see docs/environments.md.`
    )
}

const _rawSupabaseUrl = _envSupabaseUrl || PROD_SUPABASE_URL

// [DEV CORS BYPASS] When running `vite dev` against the local Supabase CLI,
// route all Supabase requests through Vite's proxy (configured in vite.config.js)
// so the browser treats them as same-origin. Production builds and dev against
// remote Supabase are unaffected.
const _isLocalSupabase = /(?:127\.0\.0\.1|localhost):54321/.test(_rawSupabaseUrl)
export const supabaseUrl = (import.meta.env.DEV && _isLocalSupabase && typeof window !== 'undefined')
    ? window.location.origin
    : _rawSupabaseUrl
export const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpwYXdhemV2c3JydXBwc3NmaHFwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgzNTc1MjQsImV4cCI6MjA4MzkzMzUyNH0.7YND4CP6sT38ApiX1IED6ernvrwWvV0c16FkEiXCd8c'
const supabaseServiceKey = import.meta.env.VITE_SUPABASE_SERVICE_KEY || '' // DANGER: Do not expose in production client bundles

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        detectSessionInUrl: true
    },
    global: {
        fetch: bypassFetch
    }
});

// [CRITICAL FIX] Patch getSession() to return in-memory session instantly.
// The internal gotrue-js getSession() uses _acquireLock → _useSession → __loadSession
// which can HANG indefinitely when Chrome wakes a tab from passive state.
// Every DB call goes through: _getAccessToken() → this.auth.getSession() → HANG.
// This patch makes getSession() return immediately from our in-memory state,
// completely bypassing the gotrue-js internal locking/loading chain.
const _originalGetSession = supabase.auth.getSession.bind(supabase.auth);
supabase.auth.getSession = async function () {
    // If we have an in-memory session, return it instantly (no locks, no storage reads)
    if (typeof window !== 'undefined' && window.__authState?.session) {
        return {
            data: { session: window.__authState.session },
            error: null
        };
    }
    // No in-memory session — fall through to original (only during initial boot)
    return _originalGetSession();
};

// Only use this for specific admin tasks if absolutely necessary and running locally
export const supabaseAdmin = supabaseServiceKey ? createClient(supabaseUrl, supabaseServiceKey) : null
